import React, { useRef, useState, useEffect, useCallback, useMemo, useContext } from 'react'
import {
  type ExcelGridProps,
  getColKey,
  getColHeader,
  getColWidth,
  getColVal,
  isColNum,
  isColCenter,
  isColLink,
} from './types'
import { useExcelGridState } from './useExcelGridState'
import { ExcelGridFilterPopover } from './ExcelGridFilterPopover'
import { parseTsv, toTsv } from './tsvUtils'
import { AuthContext } from '@/features/auth/AuthContext'

export function ExcelGrid<T>({
  id,
  userId,
  data,
  columns,
  freeze = 2,
  isEditing = false,
  searchQuery,
  total,
  rowCls,
  onCellClick,
  onRowContextMenu,
  onDataChange,
  onFilterChange,
  clearFilterTrigger,
  resetWidthsTrigger,
  emptyMessage = 'Không có dòng nào',
  className = '',
  style,
}: ExcelGridProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragStartCoordRef = useRef<[number, number] | null>(null)

  const authCtx = useContext(AuthContext)
  const resolvedUserId = userId || authCtx?.user?.id || authCtx?.user?.username || 'default'

  const {
    processedData,
    sort,
    setSort,
    filters,
    setFilters,
    selection,
    setSelection,
    selectionBox,
    activeFilterCount,
    clearAllFilters,
    colWidths,
    setColWidth,
    resetColWidths,
    autoFitColumn,
  } = useExcelGridState<T>(data, columns, id, searchQuery, resolvedUserId)

  // Report filter change to parent
  useEffect(() => {
    onFilterChange?.(activeFilterCount)
  }, [activeFilterCount, onFilterChange])

  // Clear filter trigger from parent button
  useEffect(() => {
    if (clearFilterTrigger) {
      clearAllFilters()
    }
  }, [clearFilterTrigger, clearAllFilters])

  // Reset widths trigger from parent button
  useEffect(() => {
    if (resetWidthsTrigger) {
      resetColWidths()
    }
  }, [resetWidthsTrigger, resetColWidths])

  // Filter Popover state
  const [popoverState, setPopoverState] = useState<{
    colKey: string
    anchorRect: DOMRect
  } | null>(null)

  // Toast feedback
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const toastTimeoutRef = useRef<any>(null)
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current)
    toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 3000)
  }, [])

  // Column widths with dynamic resizing
  const sttWidth = 46
  const effectiveColWidths = useMemo(() => {
    return [
      sttWidth,
      ...columns.map((c) => {
        const key = getColKey(c)
        return colWidths[key] ?? getColWidth(c)
      }),
    ]
  }, [columns, colWidths])

  const totalTableWidth = useMemo(() => {
    return effectiveColWidths.reduce((a, b) => a + b, 0)
  }, [effectiveColWidths])

  // Frozen column offsets dynamically calculated from effective column widths
  const frozenOffsets = useMemo(() => {
    let acc = 0
    const offsets: number[] = []
    for (let i = 0; i < freeze; i++) {
      offsets.push(acc)
      acc += effectiveColWidths[i] || 0
    }
    return offsets
  }, [freeze, effectiveColWidths])

  // Handle resizing column width by dragging handle
  const handleResizeMouseDown = useCallback(
    (colKey: string, colIdx: number, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const startX = e.clientX
      const currentWidth = effectiveColWidths[colIdx + 1]

      const onMouseMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault()
        const delta = moveEvent.clientX - startX
        const nextWidth = Math.max(48, Math.min(640, Math.round(currentWidth + delta)))
        setColWidth(colKey, nextWidth)
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [effectiveColWidths, setColWidth],
  )

  // Cell Selection helpers
  const handleCellMouseDown = useCallback(
    (rowIndex: number, colIndex: number, e: React.MouseEvent) => {
      if (e.button !== 0) return
      const target = e.target as HTMLElement
      // If clicking inside input/select while in edit mode, don't drag-select
      if (isEditing && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) {
        setSelection({ a: [rowIndex, colIndex], b: [rowIndex, colIndex] })
        return
      }

      if (e.shiftKey && selection) {
        setSelection((prev) => (prev ? { a: prev.a, b: [rowIndex, colIndex] } : null))
        return
      }

      isDraggingRef.current = true
      dragStartCoordRef.current = [rowIndex, colIndex]
      setSelection({ a: [rowIndex, colIndex], b: [rowIndex, colIndex] })
    },
    [isEditing, selection],
  )

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (!isDraggingRef.current || !dragStartCoordRef.current) return
      setSelection({ a: dragStartCoordRef.current, b: [rowIndex, colIndex] })
    },
    [],
  )

  useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false
      dragStartCoordRef.current = null
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  // Copy handler (TSV)
  const handleCopy = useCallback(
    (e: ClipboardEvent) => {
      if (!selectionBox || !containerRef.current) return
      const activeEl = document.activeElement
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') &&
        containerRef.current.contains(activeEl)
      ) {
        // Normal text selection inside input
        const input = activeEl as HTMLInputElement
        if (input.selectionStart !== input.selectionEnd) return
      }

      // Check if grid or container is focused
      if (!containerRef.current.contains(activeEl) && document.body !== activeEl) return

      const matrix: any[][] = []
      for (let r = selectionBox.r0; r <= selectionBox.r1; r++) {
        const rowData = processedData[r]
        if (!rowData) continue
        const rowCells: any[] = []
        for (let c = selectionBox.c0; c <= selectionBox.c1; c++) {
          const colDef = columns[c]
          if (colDef) {
            rowCells.push(getColVal(colDef, rowData) ?? '')
          }
        }
        matrix.push(rowCells)
      }

      if (matrix.length > 0) {
        const tsv = toTsv(matrix)
        e.clipboardData?.setData('text/plain', tsv)
        e.preventDefault()
        showToast('Đã sao chép — dán vào Excel được')
      }
    },
    [selectionBox, processedData, columns, showToast],
  )

  // Paste handler
  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (!selectionBox || !containerRef.current) return
      const activeEl = document.activeElement
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') &&
        containerRef.current.contains(activeEl)
      ) {
        // If single input paste without multi-cell selection
        if (selectionBox.r0 === selectionBox.r1 && selectionBox.c0 === selectionBox.c1) {
          return
        }
      }

      if (!isEditing) {
        showToast('Bấm "Sửa" (hoặc "Kiểm kê") trước khi dán')
        return
      }

      const text = e.clipboardData?.getData('text')
      if (!text) return
      const matrix = parseTsv(text)
      if (matrix.length === 0) return

      e.preventDefault()
      let updatedCount = 0
      const errors: string[] = []

      const isSingleVal = matrix.length === 1 && matrix[0].length === 1
      const isMultiCellBox =
        selectionBox.r1 > selectionBox.r0 || selectionBox.c1 > selectionBox.c0

      if (isSingleVal && isMultiCellBox) {
        // Fill whole selected range with the single value
        const val = matrix[0][0]
        for (let r = selectionBox.r0; r <= selectionBox.r1; r++) {
          const rowData = processedData[r]
          if (!rowData) continue
          for (let c = selectionBox.c0; c <= selectionBox.c1; c++) {
            const col = columns[c]
            if (col && col.edit && col.edit(rowData) && col.set) {
              const err = col.set(rowData, val)
              if (err) errors.push(err)
              else updatedCount++
            }
          }
        }
      } else {
        // Map matrix starting from selectionBox.r0, selectionBox.c0
        matrix.forEach((cells, i) => {
          const r = selectionBox.r0 + i
          const rowData = processedData[r]
          if (!rowData) return
          cells.forEach((val, j) => {
            const c = selectionBox.c0 + j
            const col = columns[c]
            if (col && col.edit && col.edit(rowData) && col.set) {
              const err = col.set(rowData, val)
              if (err) errors.push(err)
              else updatedCount++
            }
          })
        })
      }

      if (updatedCount > 0) {
        onDataChange?.()
      }
      showToast(
        `Đã dán ${updatedCount} ô${errors.length ? ` — ${errors.length} ô lỗi: ${errors[0]}` : ''}`,
      )
    },
    [selectionBox, isEditing, processedData, columns, onDataChange, showToast],
  )

  useEffect(() => {
    document.addEventListener('copy', handleCopy)
    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('copy', handleCopy)
      document.removeEventListener('paste', handlePaste)
    }
  }, [handleCopy, handlePaste])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selection) return
      const target = e.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'SELECT'

      if (e.key === 'Escape') {
        setPopoverState(null)
        return
      }

      // Enter or arrows in input moves row
      if (isInput) {
        if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          const [curR, curC] = selection.a
          const nextR =
            curR + (e.key === 'ArrowUp' || (e.key === 'Enter' && e.shiftKey) ? -1 : 1)
          if (nextR >= 0 && nextR < processedData.length) {
            setSelection({ a: [nextR, curC], b: [nextR, curC] })
            // Focus new input
            setTimeout(() => {
              const inputEl = containerRef.current?.querySelector<HTMLInputElement>(
                `input[data-r="${nextR}"][data-c="${curC}"], select[data-r="${nextR}"][data-c="${curC}"]`,
              )
              inputEl?.focus()
            }, 10)
          }
        }
        return
      }

      const numRows = processedData.length
      const numCols = columns.length
      const baseCoord = e.shiftKey ? selection.b : selection.a

      const move = (dr: number, dc: number) => {
        const nr = Math.max(0, Math.min(numRows - 1, baseCoord[0] + dr))
        const nc = Math.max(0, Math.min(numCols - 1, baseCoord[1] + dc))
        if (e.shiftKey) {
          setSelection((prev) => (prev ? { a: prev.a, b: [nr, nc] } : null))
        } else {
          setSelection({ a: [nr, nc], b: [nr, nc] })
        }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        move(1, 0)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        move(-1, 0)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        move(0, -1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        move(0, 1)
      } else if (e.key === 'Tab') {
        e.preventDefault()
        move(0, e.shiftKey ? -1 : 1)
      } else if (e.key === 'F2' || (e.key === 'Enter' && !isInput)) {
        e.preventDefault()
        const [r, c] = selection.a
        const inputEl = containerRef.current?.querySelector<HTMLInputElement>(
          `input[data-r="${r}"][data-c="${c}"], select[data-r="${r}"][data-c="${c}"]`,
        )
        inputEl?.focus()
      }
    },
    [selection, processedData.length, columns.length],
  )

  // Open filter popover
  const handleOpenFilter = (colKey: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const target = e.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    setPopoverState({ colKey, anchorRect: rect })
  }

  // Aggregate totals
  const totalRow = useMemo(() => {
    if (!total) return null
    return total(processedData)
  }, [total, processedData])

  return (
    <div
      ref={containerRef}
      className={`excel-grid-container relative flex-1 min-h-0 w-full overflow-auto bg-white select-none ${className}`}
      style={{
        fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
        fontSize: '12.5px',
        lineHeight: 1.35,
        ...style,
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <table
        className="excel-grid-table border-separate border-spacing-0"
        style={{
          tableLayout: 'fixed',
          width: totalTableWidth,
        }}
      >
        <colgroup>
          {effectiveColWidths.map((w, idx) => (
            <col key={idx} style={{ width: `${w}px` }} />
          ))}
        </colgroup>

        <thead>
          {/* Header Row */}
          <tr className="excel-grid-hdr">
            {/* STT Header */}
            <th
              className="sticky top-0 z-[8] bg-[#efefef] font-bold text-center p-1 text-[11.5px] leading-tight border-r border-b border-[#c4c4c4]"
              style={{
                left: 0,
                width: sttWidth,
                boxShadow: freeze === 1 ? '2px 0 0 #888' : undefined,
              }}
            >
              STT
            </th>

            {/* Column Headers */}
            {columns.map((col, cIdx) => {
              const colKey = getColKey(col)
              const colHeader = getColHeader(col)
              const sortIdx = sort.findIndex((s) => s.k === colKey)
              const isFiltered = Boolean(filters[colKey] && filters[colKey].length > 0)
              const isSorted = sortIdx >= 0
              const isHighlighted = isFiltered || isSorted

              let badge = '▾'
              if (isSorted) {
                const dirSym = sort[sortIdx].dir === 'desc' ? '↓' : '↑'
                badge = sort.length > 1 ? `${sortIdx + 1}${dirSym}` : dirSym
              }

              const isFrozen = cIdx + 1 < freeze
              const isLastFrozen = cIdx + 1 === freeze - 1
              const stickyLeft = isFrozen ? frozenOffsets[cIdx + 1] : undefined
              const colW = effectiveColWidths[cIdx + 1]

              return (
                <th
                  key={colKey}
                  className="sticky top-0 bg-[#efefef] font-bold text-center p-1 text-[11.5px] leading-tight border-r border-b border-[#c4c4c4] relative group"
                  style={{
                    left: stickyLeft,
                    width: colW,
                    zIndex: isFrozen ? 8 : 5,
                    boxShadow: isLastFrozen ? '2px 0 0 #888' : undefined,
                  }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="truncate">{colHeader}</span>
                  </div>
                  <button
                    type="button"
                    className={`fl block mx-auto mt-1 border rounded-[1px] cursor-pointer text-[9px] leading-[12px] font-normal transition-colors ${
                      isHighlighted
                        ? 'bg-[#222] text-white border-[#222] px-1 w-auto'
                        : 'bg-white text-[#444] border-[#aaa] w-4 h-3.5'
                    }`}
                    onClick={(e) => handleOpenFilter(colKey, e)}
                    title="Lọc & Sắp xếp"
                  >
                    {badge}
                  </button>
                  <div
                    className="excel-grid-resizer"
                    title="Kéo mép phải để đổi độ rộng, nhấp đúp để tự vừa nội dung"
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '6px',
                      height: '100%',
                      cursor: 'col-resize',
                      userSelect: 'none',
                      zIndex: 10,
                    }}
                    onMouseDown={(e) => handleResizeMouseDown(colKey, cIdx, e)}
                    onDoubleClick={(e) => {
                      e.stopPropagation()
                      autoFitColumn(colKey)
                    }}
                  />
                </th>
              )
            })}
          </tr>

          {/* Aggregate Σ Row */}
          {totalRow && (
            <tr className="excel-grid-tot">
              <td
                className="sticky z-[7] bg-[#e2e2e2] font-bold text-center border-r border-b-2 border-r-[#c4c4c4] border-b-[#777] p-1"
                style={{
                  top: '40px',
                  left: 0,
                  boxShadow: freeze === 1 ? '2px 0 0 #888' : undefined,
                }}
              >
                Σ
              </td>
              {columns.map((col, cIdx) => {
                const colKey = getColKey(col)
                const isFrozen = cIdx + 1 < freeze
                const isLastFrozen = cIdx + 1 === freeze - 1
                const stickyLeft = isFrozen ? frozenOffsets[cIdx + 1] : undefined
                const totVal = totalRow[colKey]

                return (
                  <td
                    key={colKey}
                    className={`sticky z-[7] bg-[#e2e2e2] font-bold border-r border-b-2 border-r-[#c4c4c4] border-b-[#777] px-1.5 py-1 ${
                      isColNum(col) ? 'text-right font-mono' : isColCenter(col) ? 'text-center' : 'text-left'
                    }`}
                    style={{
                      top: '40px',
                      left: stickyLeft,
                      zIndex: isFrozen ? 7 : 4,
                      boxShadow: isLastFrozen ? '2px 0 0 #888' : undefined,
                    }}
                  >
                    {typeof totVal === 'number'
                      ? totVal.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
                      : totVal ?? ''}
                  </td>
                )
              })}
            </tr>
          )}
        </thead>

        <tbody>
          {processedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="p-8 text-left text-[#777] bg-white border-b border-[#c4c4c4]"
              >
                <div className="sticky left-0 w-fit max-w-[min(100%,48rem)] pl-2">{emptyMessage}</div>
              </td>
            </tr>
          ) : (
            processedData.map((row, rIdx) => {
              const customRowCls = rowCls ? rowCls(row, rIdx) : ''

              return (
                <tr key={rIdx} className={customRowCls} data-r={rIdx} onContextMenu={(e) => onRowContextMenu?.(row, e)}>
                  {/* STT Column */}
                  <td
                    className="stt sticky z-[4] text-center bg-[#efefef] font-semibold text-[#444] font-mono border-r border-b border-[#c4c4c4] p-1"
                    style={{
                      left: 0,
                      boxShadow: freeze === 1 ? '2px 0 0 #888' : undefined,
                    }}
                  >
                    {rIdx + 1}
                  </td>

                  {/* Data Columns */}
                  {columns.map((col, cIdx) => {
                    const colKey = getColKey(col)
                    const isFrozen = cIdx + 1 < freeze
                    const isLastFrozen = cIdx + 1 === freeze - 1
                    const stickyLeft = isFrozen ? frozenOffsets[cIdx + 1] : undefined

                    const val = getColVal(col, row)
                    const canEdit = isEditing && Boolean(col.edit && col.edit(row))
                    const isReadOnlyField = isEditing && Boolean(col.edit && !canEdit)

                    // Selection highlight
                    let isSelected = false
                    let isActiveCell = false
                    if (selectionBox) {
                      isSelected =
                        rIdx >= selectionBox.r0 &&
                        rIdx <= selectionBox.r1 &&
                        cIdx >= selectionBox.c0 &&
                        cIdx <= selectionBox.c1
                    }
                    if (selection) {
                      isActiveCell = selection.a[0] === rIdx && selection.a[1] === cIdx
                    }

                    // Content
                    let content: React.ReactNode = null
                    if (canEdit) {
                      if (col.opts) {
                        const options =
                          typeof col.opts === 'function' ? col.opts(row) : col.opts
                        content = (
                          <select
                            data-r={rIdx}
                            data-c={cIdx}
                            className="w-full h-full border-0 bg-transparent px-1.5 py-1 outline-none shadow-[inset_0_0_0_1px_#8aa] focus:outline-2 focus:outline-[#1f6feb] focus:-outline-offset-2"
                            value={String(val ?? '')}
                            onChange={(e) => {
                              const err = col.set?.(row, e.target.value)
                              if (err) showToast(err)
                              onDataChange?.()
                            }}
                          >
                            <option value=""></option>
                            {options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )
                      } else {
                        content = (
                          <input
                            data-r={rIdx}
                            data-c={cIdx}
                            type="text"
                            inputMode={isColNum(col) ? 'decimal' : undefined}
                            className={`w-full h-full border-0 bg-transparent px-1.5 py-1 outline-none shadow-[inset_0_0_0_1px_#8aa] focus:outline-2 focus:outline-[#1f6feb] focus:-outline-offset-2 ${
                              isColNum(col) ? 'text-right font-mono' : ''
                            }`}
                            value={val ?? ''}
                            onChange={(e) => {
                              const err = col.set?.(row, e.target.value)
                              if (err) showToast(err)
                              onDataChange?.()
                            }}
                          />
                        )
                      }
                    } else if (col.render || (col as any).renderCell) {
                      const renderFn = col.render || (col as any).renderCell
                      content = renderFn(row, rIdx)
                    } else if (typeof val === 'number') {
                      content = val.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
                    } else {
                      content = String(val ?? '')
                    }

                    const customCellCls = col.cls ? col.cls(row) : ''
                    const tooltip =
                      col.tip
                        ? col.tip(row)
                        : (col as any).tooltip
                        ? (col as any).tooltip(row)
                        : typeof content === 'string' && content.trim()
                        ? content.trim()
                        : typeof val === 'string' && val.trim()
                        ? val.trim()
                        : typeof val === 'number'
                        ? String(val)
                        : undefined

                    return (
                      <td
                        key={colKey}
                        data-c={cIdx}
                        title={tooltip}
                        className={`overflow-hidden text-ellipsis whitespace-nowrap border-r border-b border-[#c4c4c4] ${
                          canEdit ? 'p-0 bg-white' : 'px-1.5 py-1'
                        } ${isReadOnlyField ? 'bg-[#f7f7f7] text-[#333]' : ''} ${
                          isColNum(col) ? 'text-right font-mono' : isColCenter(col) ? 'text-center' : 'text-left'
                        } ${
                          isColLink(col) && !isEditing
                            ? 'text-[#0b4fb3] cursor-pointer underline hover:text-[#002f7a]'
                            : ''
                        } ${customCellCls} ${
                          isSelected
                            ? 'bg-[linear-gradient(rgba(31,111,235,0.16),rgba(31,111,235,0.16))]'
                            : ''
                        } ${isActiveCell ? 'outline-2 outline-[#1f6feb] -outline-offset-2' : ''}`}
                        style={{
                          left: stickyLeft,
                          position: isFrozen ? 'sticky' : undefined,
                          zIndex: isFrozen ? 4 : undefined,
                          background: isSelected
                            ? undefined
                            : isFrozen
                            ? '#fff'
                            : undefined,
                          boxShadow: isLastFrozen ? '2px 0 0 #888' : undefined,
                        }}
                        onMouseDown={(e) => handleCellMouseDown(rIdx, cIdx, e)}
                        onMouseEnter={() => handleCellMouseEnter(rIdx, cIdx)}
                        onClick={(e) => {
                          if (isColLink(col) && !isEditing) {
                            onCellClick?.(row, col, e)
                          }
                        }}
                      >
                        {content}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </table>

      {/* Filter Popover */}
      {popoverState && (
        <ExcelGridFilterPopover
          column={columns.find((c) => getColKey(c) === popoverState.colKey)!}
          columns={columns}
          allRows={data}
          currentExcluded={filters[popoverState.colKey] || []}
          sort={sort}
          anchorRect={popoverState.anchorRect}
          onClose={() => setPopoverState(null)}
          onApplyFilter={(excluded) => {
            setFilters((prev) => ({
              ...prev,
              [popoverState.colKey]: excluded,
            }))
          }}
          onUpdateSort={(newSort) => setSort(newSort)}
        />
      )}

      {/* Floating Toast Message */}
      {toastMessage && (
        <div className="fixed right-3.5 bottom-3.5 bg-[#222] text-white px-3 py-2 rounded-[3px] text-xs z-[400] shadow-lg pointer-events-none transition-opacity duration-150">
          {toastMessage}
        </div>
      )}
    </div>
  )
}
